import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Mail, Phone, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  new: "bg-[#d4af37]/15 text-[#d4af37] border-[#d4af37]/30",
  contacted: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  archived: "bg-white/5 text-white/40 border-white/10",
};

export default function AdminLeads() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin");
  }, [authLoading, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (err) {
      toast.error("Could not load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  const shown = leads.filter((l) => filter === "all" || l.status === filter);
  const newCount = leads.filter((l) => l.status === "new").length;

  if (authLoading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white/40">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-leads-page">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-xl text-white">Elite</span>
            <span className="overline text-[#d4af37] hidden sm:inline">Inquiries</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-leads"
              onClick={load}
              className="p-2.5 rounded-full border border-white/15 text-white/70 hover:text-[#d4af37] hover:border-[#d4af37]/40 transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 text-white/70 hover:text-white text-xs uppercase tracking-wider transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <h1 className="font-serif font-light text-4xl md:text-5xl text-white tracking-tight">Project Inquiries</h1>
            <p className="text-white/50 mt-3 font-light">
              {leads.length} total · <span className="text-[#d4af37]">{newCount} new</span>
            </p>
          </div>
          <div className="w-full sm:w-56">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="lead-filter" className="bg-[#0a0a0a] border-white/15 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0e0e0e] border-white/10 text-white">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 py-20 text-center">Loading inquiries…</div>
        ) : shown.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-28 text-white/40 border border-white/10 rounded-sm">
            <Inbox size={40} className="mb-4 text-white/20" />
            <p>No inquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {shown.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-${l.id}`}
                className="border border-white/10 bg-[#0a0a0a] rounded-sm p-6 md:p-8 hover:border-white/20 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif text-2xl text-white">{l.name}</h3>
                      <span className={`text-[10px] uppercase tracking-wider px-3 py-1 rounded-full border ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>
                        {l.status}
                      </span>
                      {l.inquiry_type && (
                        <span className="text-[10px] uppercase tracking-wider text-[#d4af37] border border-[#d4af37]/30 px-3 py-1 rounded-full">
                          {l.inquiry_type}
                        </span>
                      )}
                      {l.project_type && (
                        <span className="text-[10px] uppercase tracking-wider text-white/50 border border-white/10 px-3 py-1 rounded-full">
                          {l.project_type}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3 text-sm text-white/60">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 hover:text-[#d4af37]">
                        <Mail size={13} /> {l.email}
                      </a>
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 hover:text-[#d4af37]">
                          <Phone size={13} /> {l.phone}
                        </a>
                      )}
                      {l.company && <span>{l.company}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 w-full md:w-44">
                    <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger data-testid={`status-${l.id}`} className="bg-[#0e0e0e] border-white/15 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0e0e0e] border-white/10 text-white">
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="mt-5 text-white/70 font-light leading-relaxed whitespace-pre-wrap border-t border-white/10 pt-5">
                  {l.message}
                </p>
                <p className="mt-4 text-white/30 text-xs">
                  {new Date(l.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
